// src/pages/PricingPage.js
import React, { useState, useEffect } from "react";
import axiosInstance from "../api/axiosInstance";
import PagesTopNav from "../components/TopNav/PagesTopNav";
import { useAuth } from "../context/AuthContext";
import { FaEdit, FaSave, FaTimes } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PageLayout from "../components/PageLayout";
import styled from "styled-components";

// Inline alert (no toast for visitors)
const InlineAlert = styled.div`
  margin: 0 0 1rem;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: .95rem;
  border: 1px solid ${(p) => (p.$variant === "error" ? "#fbd5d5" : "#c7e2ff")};
  background: ${(p) => (p.$variant === "error" ? "#fde8e8" : "#eef6ff")};
  color: ${(p) => (p.$variant === "error" ? "#9b1c1c" : "#1e3a8a")};
`;

const PricingPage = () => {
  const label = "pricing";
  const [content, setContent] = useState("Loading…");
  const [editing, setEditing] = useState(false);
  const [editedContent, setEditedContent] = useState("");
  const [loadError, setLoadError] = useState("");

  const { authData } = useAuth();
  const roles = authData?.role;
  const isAdmin = Array.isArray(roles)
    ? roles.includes("SystemAdmin")
    : roles === "SystemAdmin";

  useEffect(() => {
    let mounted = true;
    axiosInstance
      .get(`/PageContent/${label}`)
      .then((res) => {
        if (!mounted) return;
        setContent(res.data?.content || "Default pricing content.");
        setLoadError("");
      })
      .catch((err) => {
        console.error("Failed to load pricing content:", err);
        if (!mounted) return;
        // No toast for visitors — inline message instead
        setLoadError("We’re having trouble loading pricing right now.");
      });
    return () => { mounted = false; };
  }, [label]);

  const handleEdit = () => {
    if (!isAdmin) return;
    setEditedContent(content);
    setEditing(true);
  };

  const handleCancel = () => {
    setEditing(false);
    setEditedContent("");
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!isAdmin) return;
    try {
      await axiosInstance.put(
        `/PageContent/${label}`,
        { content: editedContent },
        {
          headers: {
            Authorization: `Bearer ${authData?.token}`,
            "Content-Type": "application/json",
          },
        }
      );
      setContent(editedContent);
      setEditing(false);
      // Admin-only feedback
      toast.success("Content updated successfully!");
    } catch (error) {
      console.error("Failed to update pricing content:", error);
      toast.error("Failed to update content.");
    }
  };

  return (
    <div>
      <PagesTopNav />
      {isAdmin && <ToastContainer />}

      <PageLayout title="Pricing Plans" description="Choose the best plan for your needs.">
        {loadError && (
          <InlineAlert role="status" aria-live="polite" $variant="error">
            {loadError}
          </InlineAlert>
        )}

        {editing ? (
          <div className="edit-container">
            <textarea
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              rows={5}
              className="edit-textarea"
              aria-label="Edit Pricing Content"
            />
            <div className="edit-controls">
              <button onClick={handleSave} className="icon-save" aria-label="Save">
                <FaSave />
              </button>
              <button onClick={handleCancel} className="icon-cancel" aria-label="Cancel">
                <FaTimes />
              </button>
            </div>
          </div>
        ) : (
          <div className="content-display">
            <p>{content}</p>
            {isAdmin && (
              <button onClick={handleEdit} className="icon-edit" aria-label="Edit">
                <FaEdit />
              </button>
            )}
          </div>
        )}
      </PageLayout>
    </div>
  );
};

export default PricingPage;
